import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsTrigger } from '../ui/tabs';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menubar,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Briefcase,
  Search,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  MapPin,
  Calendar,
  Building2,
  Filter,
  Download,
  Upload,
  Plus,
  Star,
  Crown,
  Gem,
  Camera,
  Image as ImageIcon
} from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem } from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { normalizeMediaUrl } from '../../utils/media';
import { RichTextEditor } from '../ui/rich-text-editor';
import { getJobParameters, fetchJobParameters } from '../../services/jobParametersService';
import { normalizeTier, getTierLabel } from '../../utils/tier';
import { BranchSelector } from './BranchSelector';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { useLocation, useNavigate } from 'react-router-dom';
import { useImageUpdate } from '../../context/ImageUpdateContext';
import { useApp } from '@/pages/providers/AppProvider';
import DOMPurify from 'dompurify';
import { stripHtml, decodeHtml } from '@/utils/text';

interface JobPostManagementProps {
  jobs: any[];
  companies: any[];
  onJobUpdate: (jobs: any[]) => void;
  onCompanyUpdate?: (companies: any[]) => void;
}

const fieldCls = 'w-full h-10 px-3 py-2 rounded-md border border-gray-200 bg-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300';
const textareaCls = 'w-full min-h-[140px] px-3 py-2 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300';
const boxLabelCls = 'px-3 text-gray-700 text-sm font-semibold border-r flex items-center whitespace-nowrap';

type BoxProps = { label: string; children: React.ReactNode };

const Box = ({ label, children }: BoxProps) => (
  <div className="w-full bg-white border border-gray-200 rounded-md p-2 flex items-center gap-2">
    <div className={boxLabelCls}>{label}</div>
    <div className="flex-1">
      {children}
    </div>
  </div>
);

const BoxAuto = ({ label, children }: BoxProps) => {
  const hasLabel = !!(label && label.trim().length > 0);
  return (
    <div className="w-full bg-gray-50 border border-gray-200 rounded-md p-2 relative">
      {hasLabel && (
        <div className="text-gray-600 text-sm px-1 pointer-events-none">{label}</div>
      )}
      <div className={hasLabel ? "mt-2" : ""}>
        {children}
      </div>
    </div>
  );
};

export function JobPostManagement({ jobs, companies, onJobUpdate, onCompanyUpdate }: JobPostManagementProps) {
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || params.get('search') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('status') || 'active';
  });
  const [tierFilter, setTierFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tier') || 'all';
  });
  const [companyFilter, setCompanyFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('company') || 'all';
  });
  const [activeTab, setActiveTab] = useState<'all-jobs' | 'add-job' | 'pending' | 'tier-management'>(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'add-job') return 'add-job';
    if (tab === 'pending') return 'pending';
    if (tab === 'tier-management') return 'tier-management';
    return 'all-jobs';
  });
  const [editingJob, setEditingJob] = useState<any>(null);
  const [editingCoverFile, setEditingCoverFile] = useState<File | null>(null);
  const [isUpdatingJob, setIsUpdatingJob] = useState(false);
  const [viewingJob, setViewingJob] = useState<any>(null);
  const [jobExportFrom, setJobExportFrom] = useState('');
  const [jobExportTo, setJobExportTo] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { setJobs: setGlobalJobs } = useApp();
  const [parameters, setParameters] = useState(getJobParameters());
  const [newCoverDims, setNewCoverDims] = useState<{ w: number; h: number } | null>(null);
  const { triggerImageUpdate } = useImageUpdate();
  const now = React.useMemo(() => new Date(), []);
  const [isCreatingJob, setIsCreatingJob] = useState(false);

  // Refresh parameters from server on mount
  React.useEffect(() => {
    const sync = async () => {
      try {
        const latest = await fetchJobParameters();
        setParameters(latest);
      } catch (err) {
        console.warn('Failed to sync job parameters in JobPostManagement:', err);
      }
    };
    sync();
  }, []);

  // Prevent body scroll when edit form is open
  React.useEffect(() => {
    if (editingJob) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [editingJob]);

  const titleRef = React.useRef<HTMLInputElement>(null);
  const descRef = React.useRef<HTMLTextAreaElement>(null);
  const specRef = React.useRef<HTMLTextAreaElement>(null);
  const locationRef = React.useRef<HTMLInputElement>(null);
  const editTitleRef = React.useRef<HTMLInputElement>(null);

  // Sync state with URL params
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || params.get('search');
    const s = params.get('status');
    const t = params.get('tier');
    const c = params.get('company');
    const tab = params.get('tab');

    if (q !== null) setSearchTerm(q);
    if (s !== null) setStatusFilter(s);
    if (t !== null) setTierFilter(t);
    if (c !== null) setCompanyFilter(c);
    if (tab !== null) {
      if (tab === 'add-job') setActiveTab('add-job');
      else if (tab === 'pending') setActiveTab('pending');
      else if (tab === 'tier-management') setActiveTab('tier-management');
      else setActiveTab('all-jobs');
    }
  }, [location.search]);
  const editCompanyRef = React.useRef<HTMLInputElement>(null);
  const editDescRef = React.useRef<HTMLTextAreaElement>(null);
  const editSalaryMinRef = React.useRef<HTMLInputElement>(null);
  const editSalaryMaxRef = React.useRef<HTMLInputElement>(null);

  const [newJob, setNewJob] = useState({
    title: '',
    company: '',
    location: '',
    description: '',
    requirements: '',
    salary: '',
    // structured salary fields
    salaryType: 'negotiable',
    salaryMin: '',
    salaryMax: '',
    payType: 'Monthly',
    tier: 'latest',
    type: 'Full Time',
    category: '',
    experience: '',
    jobLevel: '',
    desiredCandidate: '',
    skillCategory: '',
    skillName: '',
    selectedSkills: [] as string[],
    licenseRequired: false,
    vehicleType: 'Not Required',
    deadline: '',
    featured: false,
    urgent: false,
    coverImage: null as File | null
  });



  // Enhanced job data with normalized fields for both display and editing
  const enhancedJobs = jobs.map(job => {
    // Normalize tier
    const normalizedTier = normalizeTier(job.tier) || 'latest';

    // Normalize experience
    const rawExp = job.experience || job.experience_level || '';
    const experience = rawExp === 'entry' ? 'Entry Level' :
      rawExp === 'mid' ? 'Mid Level' :
        rawExp === 'senior' ? 'Senior Level' :
          rawExp === 'executive' ? 'Executive' : rawExp;

    // Normalize employment type
    const rawType = job.type || job.employment_type || 'Full Time';
    const displayType = rawType === 'full_time' ? 'Full Time' :
      rawType === 'part_time' ? 'Part Time' :
        rawType === 'contract' ? 'Contract' :
          rawType === 'internship' ? 'Internship' : rawType;

    // Normalize desired candidate
    const rawCandidate = job.desiredCandidate || job.desired_candidate || '';
    const desiredCandidate = rawCandidate.charAt(0).toUpperCase() + rawCandidate.slice(1).toLowerCase();
    const finalCandidate = ['Male', 'Female', 'Others', 'Both'].includes(desiredCandidate) ? desiredCandidate :
      (rawCandidate === 'both' ? 'Both' : (rawCandidate || ''));

    // Normalize vehicle/license info
    const vehicleType = job.vehicle_type || job.vehicleType || 'Not Required';
    const licenseRequired = typeof job.license_required === 'boolean' ? job.license_required :
      (typeof job.licenseRequired === 'boolean' ? job.licenseRequired : vehicleType !== 'Not Required');

    return {
      ...job,
      id: job.id || job._id,
      tier: normalizedTier,
      type: displayType,
      experience: experience,
      jobLevel: job.jobLevel || job.job_level || '',
      desiredCandidate: finalCandidate,
      applicationCount: typeof job.applicationCount === 'number' ? job.applicationCount : 0,
      viewCount: typeof job.viewCount === 'number' ? job.viewCount : 0,
      featured: !!job.featured,
      urgent: !!job.urgent,
      licenseRequired,
      vehicleType,
      postedDate: job.postedDate || job.created_at || new Date().toISOString().split('T')[0],
      expiryDate: job.expiryDate || job.deadline || job.expiry_date || '',
      deadline: job.deadline || job.expiryDate || job.expiry_date || '',
      status: job.status || 'active',
      // Ensure salary fields are accessible
      salaryMin: job.salaryMin || job.salary_min || '',
      salaryMax: job.salaryMax || job.salary_max || '',
      salaryType: job.salaryType || job.salary_type || (job.salary_min && job.salary_max ? 'range' : (job.salary ? 'exact' : 'negotiable')),
      payType: job.payType || job.pay_type || 'Monthly',
      // Normalize skills - prefer skills or selectedSkills field
      selectedSkills: Array.isArray(job.skills) ? job.skills :
        (Array.isArray(job.selectedSkills) ? job.selectedSkills : [])
    };
  });

  const isExpired = React.useCallback((j: any) => {
    // If explicitly marked as expired or closed, it is definitely expired
    if (j?.status === 'expired' || j?.status === 'closed') return true;

    // For other statuses (like rejected), we usually don't want to call them "expired" 
    // in the UI if their primary status is more important.
    // But for the sake of the filter logic, we check the date.
    const raw = j?.expiryDate || j?.expiry_date || j?.deadline;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;

    // Set expiry to the end of the day (23:59:59) so it doesn't expire prematurely
    const expiryTime = new Date(d);
    expiryTime.setHours(23, 59, 59, 999);

    return expiryTime.getTime() <= now.getTime();
  }, [now]);

  // Removed auto-focus/selection management to prevent caret jumps while typing

  // Remove edit auto-focus to prevent cursor jumps while typing

  const getJobImage = (job: any): string => {
    const primary = (job as any).coverImageUrl || (job as any).cover_image_url || (job as any).logo;
    if (primary) {
      const v = normalizeMediaUrl(primary) || primary;
      return String(v || '').trim();
    }
    const companyName = String(job?.company || '').trim().toLowerCase();
    if (companyName && Array.isArray(companies)) {
      const found = companies.find((c: any) => String(c?.name || '').trim().toLowerCase() === companyName);
      if (found) {
        const logo = (found as any).logo_url || (found as any).logoUrl || (found as any).logo || (found as any).profileImage;
        if (logo) {
          const v = normalizeMediaUrl(logo) || logo;
          return String(v || '').trim();
        }
      }
    }
    return '';
  };

  const [skillsDraft, setSkillsDraft] = useState<string[]>([]);
  React.useEffect(() => { setSkillsDraft(newJob.selectedSkills || []); }, []);
  const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);
  const [skillsQuery, setSkillsQuery] = useState('');
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const isInvalid = (name: string) => invalidFields.includes(name);

  const formatCompanyAddress = (company: any): string => {
    const rawParts = [
      String(company?.location || '').trim(),
      String(company?.address || '').trim(),
      String(company?.city || '').trim(),
      String(company?.province || company?.state || '').trim(),
      String(company?.country || '').trim()
    ].filter(Boolean);
    const tokens: string[] = [];
    const seen = new Set<string>();
    rawParts
      .flatMap(p => String(p).split(/[,-]/).map(s => s.trim()).filter(Boolean))
      .forEach(tok => { const k = tok.toLowerCase(); if (!seen.has(k)) { seen.add(k); tokens.push(tok); } });
    return tokens.join(', ');
  };

  React.useEffect(() => {
    if (!newJob.company) return;
    try {
      const companyObj: any = (companies || []).find((c: any) => String(c?.name || '').trim() === String(newJob.company || '').trim());
      if (!companyObj) return;
      const branches = (companyObj?.branches || []) as any[];
      if ((!branches || branches.length === 0) && !newJob.location) {
        const addr = formatCompanyAddress(companyObj);
        if (addr) setNewJob(prev => ({ ...prev, location: addr }));
      }
    } catch { }
  }, [newJob.company]);

  const getSalaryDisplay = (job: any): string => {
    const str = (v: any) => (typeof v === 'string' ? v.trim() : String(v ?? '')).trim();
    const parseNum = (v: any) => {
      const n = parseInt(String(v).replace(/[^0-9]/g, ''));
      return Number.isFinite(n) ? n : null;
    };
    const fmt = (n: number) => `NPR ${n.toLocaleString()}`;
    const payType = job?.payType || job?.pay_type;
    const salaryTypeRaw = String(job?.salaryType || job?.salary_type || '').trim();
    const salaryType = salaryTypeRaw.toLowerCase();

    const fromRangeStr = job?.salary_range || job?.salaryRange;
    if (typeof fromRangeStr === 'string' && fromRangeStr.trim().length > 0) {
      const val = fromRangeStr.trim();
      if ((val.toLowerCase() === 'competitive' || val.toLowerCase() === 'negotiable') && payType) {
        return `${val} (${payType})`;
      }
      return val;
    }

    if (salaryType === 'negotiable' || salaryType.includes('negotiable')) return payType ? `Negotiable (${payType})` : 'Negotiable';
    if (salaryType === 'competitive' || salaryType.includes('competitive')) return payType ? `Competitive Salary (${payType})` : 'Competitive Salary';

    const min = job?.salary_min ?? job?.salaryMin;
    const max = job?.salary_max ?? job?.salaryMax;
    const nmin = parseNum(min);
    const nmax = parseNum(max);
    if (nmin && nmax) return payType ? `${fmt(nmin)} - ${fmt(nmax)} (${payType})` : `${fmt(nmin)} - ${fmt(nmax)}`;

    const exact = job?.salary;
    const nexact = parseNum(exact);
    if (nexact) return payType ? `${fmt(nexact)} (${payType})` : fmt(nexact);
    if (typeof exact === 'string' && exact.trim().length > 0) return exact.trim();

    const offered = job?.offered_salary || job?.offeredSalary;
    if (offered) return str(offered);

    return payType ? `Negotiable (${payType})` : 'Negotiable';
  };

  const getApplicationsButtonClass = (count: number): string => {
    if (count > 0) {
      return 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm';
    }
    return 'bg-gray-100 text-gray-800 hover:bg-gray-200 border border-gray-300';
  };

  const openApplications = (job: any) => {
    const search = new URLSearchParams(location.search);
    search.set('section', 'applied-users');
    search.set('jobId', String(job.id));
    navigate({ pathname: location.pathname, search: `?${search.toString()}` });
  };


  const jobTiers = [
    { key: 'megajob', label: 'MegaJob', price: 'Free', color: 'bg-red-100 text-red-800', icon: Crown, description: 'Premium placement with maximum visibility' },
    { key: 'premium', label: 'Premium Job', price: 'Free', color: 'bg-blue-100 text-blue-800', icon: Star, description: 'Enhanced visibility and special highlighting' },
    { key: 'prime', label: 'Prime Job', price: 'Free', color: 'bg-purple-100 text-purple-800', icon: Gem, description: 'Featured placement with priority listing' },
    { key: 'latest', label: 'Latest Jobs', price: 'Free', color: 'bg-green-100 text-green-800', icon: Briefcase, description: 'Standard job posting' },
    { key: 'newspaper', label: 'Newspaper Jobs', price: 'Free', color: 'bg-gray-100 text-gray-800', icon: Briefcase, description: 'Basic listing for newspaper jobs' }
  ];

  const filteredJobs = enhancedJobs.filter(job => {
    const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = (() => {
      if (statusFilter === 'all') return true;

      const expired = isExpired(job);

      // PRIORITY 1: If filtering for expired jobs, show all jobs with past deadlines
      // regardless of their status (rejected, pending, active, etc.)
      if (statusFilter === 'expired') return expired;

      // PRIORITY 2: If the job is expired (deadline passed), it should ONLY appear
      // in the Expired section, not in any other status section
      if (expired) return false;

      // PRIORITY 3: For non-expired jobs, filter by their actual status
      return job.status === statusFilter;
    })();
    const matchesTier = tierFilter === 'all' || normalizeTier(job.tier) === tierFilter;
    const matchesCompany = companyFilter === 'all' || job.company === companyFilter;
    return matchesSearch && matchesStatus && matchesTier && matchesCompany;
  });

  // Pagination state for Jobs table
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage) || 1;
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to first page when filters/search change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, tierFilter, companyFilter, itemsPerPage]);

  const handleApproveJob = (jobId: string) => {
    const updatedJobs = jobs.map(job =>
      job.id === jobId ? { ...job, status: 'active' } : job
    );
    onJobUpdate(updatedJobs);
    toast.success('Job approved successfully!');
  };

  // Reactivate an expired job (set status to active)
  const handleReactivateJob = (jobId: string) => {
    const updatedJobs = jobs.map(job =>
      job.id === jobId ? { ...job, status: 'active' } : job
    );
    onJobUpdate(updatedJobs);
    toast.success('Job reactivated successfully!');
  };

  const handleRejectJob = (jobId: string) => {
    const updatedJobs = jobs.map(job =>
      job.id === jobId ? { ...job, status: 'rejected' } : job
    );
    onJobUpdate(updatedJobs);
    toast.success('Job rejected');
  };

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; jobId: string | null; jobTitle: string }>({ open: false, jobId: null, jobTitle: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteJob = async () => {
    if (!deleteDialog.jobId) return;

    setIsDeleting(true);
    try {
      await apiClient.deleteJobByAdmin(String(deleteDialog.jobId));
      const updatedJobs = jobs.filter(job => (job.id !== deleteDialog.jobId) && (String((job as any)._id || '') !== String(deleteDialog.jobId)));
      onJobUpdate(updatedJobs);
      toast.success('Job deleted successfully!');
      setDeleteDialog({ open: false, jobId: null, jobTitle: '' });
    } catch (error) {
      toast.error('Failed to delete job');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateJob = async () => {
    if (!editingJob) return;
    setIsUpdatingJob(true);
    let updatedCoverUrl = editingJob.coverImageUrl || (editingJob as any).cover_image_url;
    try {
      if (editingCoverFile) {
        const jobId = editingJob.id || editingJob._id;
        const res = await apiClient.uploadAdminJobCover(String(jobId), editingCoverFile);
        const uploadedUrl = res?.job?.cover_image_url || res?.upload?.secure_url || res?.upload?.url || res?.url;
        if (uploadedUrl) {
          updatedCoverUrl = uploadedUrl;
        }
      }
    } catch (err: any) {
      console.warn('Cover upload failed:', err?.message || err);
      toast.error('Cover image upload failed');
    }
    const formatNumber = (val: string) => {
      const num = Number(val);
      if (Number.isNaN(num)) return val;
      return num.toLocaleString();
    };
    let salaryFormatted = '';
    const payType = (editingJob?.payType || editingJob?.pay_type || 'Monthly');
    const salaryTypeCanon = String(editingJob?.salaryType || editingJob?.salary_type || '').toLowerCase();
    const typeMap: Record<string, string> = {
      'Full Time': 'full_time',
      'Part Time': 'part_time',
      'Contract': 'contract',
      'Internship': 'internship'
    };
    const expMap: Record<string, string> = {
      'Entry Level': 'entry',
      'Mid Level': 'mid',
      'Senior Level': 'senior',
      'Executive': 'executive'
    };
    if (salaryTypeCanon === 'negotiable') {
      salaryFormatted = `Negotiable (${payType})`;
    } else if ((salaryTypeCanon === 'range' || (!salaryTypeCanon && editingJob?.salaryMin && editingJob?.salaryMax))) {
      const min = formatNumber(String(editingJob?.salaryMin || editingJob?.salary_min || ''));
      const max = formatNumber(String(editingJob?.salaryMax || editingJob?.salary_max || ''));
      if (min && max) salaryFormatted = `NPR ${min} - ${max} (${payType})`;
    }
    try {
      const toNum = (v: any) => {
        const n = parseInt(String(v ?? '').replace(/[^0-9]/g, ''));
        return Number.isFinite(n) ? n : undefined;
      };
      const skillsArr = (() => {
        const selected = (editingJob as any).selectedSkills;
        if (Array.isArray(selected) && selected.length > 0) return selected;
        const existing = (editingJob as any).skills;
        return Array.isArray(existing) ? existing : [];
      })();
      const payload: any = {
        title: editingJob.title,
        description: editingJob.description,
        requirements: editingJob.requirements,
        cover_image_url: updatedCoverUrl,
        pay_type: payType,
        salary_type: salaryTypeCanon || (editingJob?.salaryMin && editingJob?.salaryMax ? 'range' : 'negotiable'),
        salary_min: salaryTypeCanon === 'range' ? toNum(editingJob?.salaryMin || (editingJob as any).salary_min) : null,
        salary_max: salaryTypeCanon === 'range' ? toNum(editingJob?.salaryMax || (editingJob as any).salary_max) : null,
        salary_range: salaryFormatted || (editingJob as any).salary_range || (editingJob as any).salary || undefined,
        salary_currency: 'NPR',
        employment_type: typeMap[(editingJob as any).type] || (editingJob as any).employment_type || 'full_time',
        experience_level: expMap[(editingJob as any).experience] || String((editingJob as any).experience || '').toLowerCase() || undefined,
        tier: normalizeTier((editingJob as any).tier) || 'latest',
        desired_candidate: (editingJob as any).desiredCandidate || undefined,
        job_level: (editingJob as any).jobLevel || undefined,
        license_required: (editingJob as any).licenseRequired || ((editingJob as any).vehicleType && (editingJob as any).vehicleType !== 'Not Required') ? true : false,
        vehicle_type: (editingJob as any).vehicleType && (editingJob as any).vehicleType !== 'Not Required' ? (editingJob as any).vehicleType : undefined,
        deadline: (editingJob as any).expiryDate || (editingJob as any).expiry_date || (editingJob as any).deadline || undefined,
        skills: skillsArr
      };
      const jid = editingJob.id || (editingJob as any)._id;
      let updatedServerJob = null;
      if (jid) {
        const response = await apiClient.updateJobByAdmin(String(jid), payload);
        // Extract job from response (backend returns { message, job })
        updatedServerJob = (response as any)?.job || response;
      }

      // If server returned updated job, merge it into our list
      if (updatedServerJob) {
        const authoritative = {
          ...updatedServerJob,
          id: updatedServerJob._id || updatedServerJob.id,
          // Ensure critical fields are present
          status: updatedServerJob.status || 'active',
          approval_status: updatedServerJob.approval_status || 'approved',
          tier: normalizeTier(updatedServerJob.tier) || 'latest',
          deadline: updatedServerJob.deadline,
          expiryDate: updatedServerJob.deadline || updatedServerJob.expiryDate,
          // Preserve UI-friendly fields
          coverImageUrl: updatedServerJob.cover_image_url || updatedCoverUrl,
          cover_image_url: updatedServerJob.cover_image_url || updatedCoverUrl,
          salary: salaryFormatted || updatedServerJob.salary_range || updatedServerJob.salary,
          salary_range: salaryFormatted || updatedServerJob.salary_range,
          postedDate: updatedServerJob.postedDate || updatedServerJob.created_at || new Date().toISOString().split('T')[0]
        };

        const updatedJobs = jobs.map(j => {
          const matches = (j.id === authoritative.id || j._id === authoritative._id ||
            String(j.id) === String(authoritative.id) || String(j._id) === String(authoritative._id));
          return matches ? authoritative : j;
        });

        setGlobalJobs(updatedJobs);
        onJobUpdate(updatedJobs);
        triggerImageUpdate();
        setEditingJob(null);
        setEditingCoverFile(null);
        setIsUpdatingJob(false);
        toast.success('Job updated successfully!');
        return;
      }
    } catch (err: any) {
      console.warn('Persist job update failed:', err);
      const msg = err?.data?.message || err?.message || 'Failed to update job';
      toast.error(msg);
      setIsUpdatingJob(false);
      return;
    }
    const updatedJobs = jobs.map(job => {
      const matches = job.id === editingJob.id || job._id === (editingJob as any)._id;
      if (!matches) return job;
      const next = {
        ...editingJob,
        tier: normalizeTier(editingJob.tier) || 'latest',
        coverImageUrl: updatedCoverUrl,
        // Also set snake_case for backend persistence and other mappers
        cover_image_url: updatedCoverUrl,
        salary: salaryFormatted || editingJob?.salary || job?.salary,
        salary_range: salaryFormatted || editingJob?.salary_range || job?.salary_range,
        salary_min: salaryTypeCanon === 'range' ? (editingJob?.salaryMin || (editingJob as any).salary_min) : null,
        salary_max: salaryTypeCanon === 'range' ? (editingJob?.salaryMax || (editingJob as any).salary_max) : null,
        salaryType: salaryTypeCanon || 'negotiable',
        salary_type: salaryTypeCanon || 'negotiable',
        employment_type: typeMap[(editingJob as any).type] || (editingJob as any).employment_type || job?.employment_type,
        experience_level: expMap[(editingJob as any).experience] || String((editingJob as any).experience || '').toLowerCase() || job?.experience_level,
        desired_candidate: (editingJob as any).desiredCandidate || job?.desired_candidate,
        deadline: (editingJob as any).expiryDate || (editingJob as any).expiry_date || (editingJob as any).deadline || job?.deadline,
        skills: Array.isArray((editingJob as any).selectedSkills) && (editingJob as any).selectedSkills.length > 0
          ? (editingJob as any).selectedSkills
          : (Array.isArray((editingJob as any).skills) ? (editingJob as any).skills : job?.skills)
      } as any;
      return next;
    });
    setGlobalJobs(updatedJobs);
    onJobUpdate(updatedJobs);
    triggerImageUpdate();
    setEditingJob(null);
    setEditingCoverFile(null);
    setIsUpdatingJob(false);
    toast.success('Job updated successfully!');
  };

  const handleCreateJob = async () => {
    if (!newJob.title || !newJob.company || !newJob.location) {
      const missing: string[] = [];
      if (!newJob.title) missing.push('jobTitle');
      if (!newJob.company) missing.push('jobCompany');
      if (!newJob.location) missing.push('jobLocation');
      setInvalidFields(missing);
      // scroll to first missing field for quick fix
      try {
        const id = missing[0];
        const el = id ? document.getElementById(id) : null;
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch { }
      toast.error('Please fill in all required fields');
      return;
    }

    // Check local tracking state to prevent double clicks if button disabled attribute hasn't updated yet
    if (isCreatingJob) return;

    setIsCreatingJob(true);
    try {
      const formatNumber = (val: string) => {
        const num = Number(val);
        if (Number.isNaN(num)) return val;
        return num.toLocaleString();
      };

      const typeMap: Record<string, string> = {
        'Full Time': 'full_time',
        'Part Time': 'part_time',
        'Contract': 'contract',
        'Internship': 'internship'
      };
      const expMap: Record<string, string> = {
        'Entry Level': 'entry',
        'Mid Level': 'mid',
        'Senior Level': 'senior',
        'Executive': 'executive'
      };

      let salaryFormatted = '';
      if (newJob.salaryType === 'negotiable') {
        salaryFormatted = `Negotiable (${newJob.payType})`;
      } else if (newJob.salaryType === 'range' && newJob.salaryMin && newJob.salaryMax) {
        salaryFormatted = `NPR ${formatNumber(newJob.salaryMin)} - ${formatNumber(newJob.salaryMax)} (${newJob.payType})`;
      } else if (newJob.salary) {
        salaryFormatted = `NPR ${formatNumber(newJob.salary)} (${newJob.payType})`;
      }

      const payload: any = {
        title: newJob.title,
        description: newJob.description,
        requirements: newJob.requirements || '',
        location: newJob.location,
        company: newJob.company,
        employment_type: typeMap[newJob.type] || 'full_time',
        experience_level: expMap[newJob.experience] || (newJob.experience?.toLowerCase() || 'entry'),
        tier: normalizeTier(newJob.tier) || 'latest',
        featured: !!newJob.featured,
        license_required: newJob.vehicleType && newJob.vehicleType !== 'Not Required' ? true : false,
        vehicle_type: newJob.vehicleType && newJob.vehicleType !== 'Not Required' ? newJob.vehicleType : undefined,
        desired_candidate: newJob.desiredCandidate || undefined,
        job_level: newJob.jobLevel || undefined,
        deadline: newJob.deadline || undefined,
        skills: Array.isArray(newJob.selectedSkills) && newJob.selectedSkills.length > 0
          ? newJob.selectedSkills
          : (newJob.skillName ? [newJob.skillName] : []),
        // send formatted salary string for backend persistence/display
        salary_range: salaryFormatted || undefined,
        // also persist structured salary fields so public API can compute reliably
        salary_type: (newJob.salaryType === 'negotiable' ? 'negotiable' : (newJob.salaryType === 'range' ? 'range' : 'exact')),
        salary_min: (newJob.salaryType === 'range' && newJob.salaryMin) ? Number(String(newJob.salaryMin).replace(/[^0-9]/g, '')) : undefined,
        salary_max: (newJob.salaryType === 'range' && newJob.salaryMax) ? Number(String(newJob.salaryMax).replace(/[^0-9]/g, '')) : undefined,
        salary_currency: 'NPR',
        pay_type: newJob.payType
      };
      try {
        const companyObj = companies.find((c: any) => String(c?.name || '').trim() === String(newJob.company || '').trim());
        const compId = (companyObj as any)?._id || (companyObj as any)?.id;
        if (compId) (payload as any).company_id = compId;
      } catch { }

      const res = await apiClient.createJobByAdmin(payload);
      const createdJob = (res?.job || res);
      let coverUrl: string | null = null;

      if (newJob.coverImage && createdJob?._id) {
        try {
          const uploadRes = await apiClient.uploadAdminJobCover(String(createdJob._id), newJob.coverImage);
          coverUrl = uploadRes?.job?.cover_image_url || uploadRes?.upload?.secure_url || uploadRes?.upload?.url || null;
        } catch (e: any) {
          console.warn('Cover upload failed:', e?.message || e);
          toast.error('Cover image upload failed');
        }
      }

      const uiJob = {
        id: createdJob?._id || Date.now().toString(),
        title: createdJob?.title || newJob.title,
        company: createdJob?.company || newJob.company,
        location: createdJob?.location || newJob.location,
        description: createdJob?.description || newJob.description,
        requirements: Array.isArray(createdJob?.requirements) ? createdJob.requirements : payload.requirements,
        tier: normalizeTier(createdJob?.tier || newJob.tier) || 'latest',
        featured: !!(createdJob?.featured ?? newJob.featured),
        urgent: !!newJob.urgent,
        licenseRequired: !!(createdJob?.license_required ?? (createdJob?.vehicle_type ? true : false)),
        vehicleType: createdJob?.vehicle_type || 'Not Required',
        desiredCandidate: createdJob?.desired_candidate || newJob.desiredCandidate || '',
        deadline: createdJob?.deadline || newJob.deadline,
        status: createdJob?.status || 'active',
        applicationCount: 0,
        viewCount: 0,
        coverImageUrl: coverUrl || createdJob?.cover_image_url || null,
        skills: Array.isArray(createdJob?.skills) ? createdJob.skills : payload.skills,
        jobLevel: newJob.jobLevel,
        // reflect salary on UI list
        salary: createdJob?.salary || createdJob?.salary_range || salaryFormatted
      } as any;

      try {
        const refreshed = await apiClient.getAdminJobs({ limit: 100 });
        const list = Array.isArray((refreshed as any)?.jobs)
          ? (refreshed as any).jobs
          : Array.isArray(refreshed)
            ? (refreshed as any)
            : [];
        const mapped = list.map((j: any) => ({ ...j, id: j._id || j.id }));
        setGlobalJobs(mapped);
        onJobUpdate(mapped);
      } catch {
        onJobUpdate([...jobs, uiJob]);
      }
      triggerImageUpdate();

      // reset form
      setNewJob({
        title: '',
        company: '',
        location: '',
        description: '',
        requirements: '',
        salary: '',
        salaryType: 'negotiable',
        salaryMin: '',
        salaryMax: '',
        payType: 'Monthly',
        tier: 'latest',
        type: 'Full Time',
        category: '',
        experience: '',
        jobLevel: '',
        desiredCandidate: '',
        skillCategory: '',
        skillName: '',
        selectedSkills: [] as string[],
        licenseRequired: false,
        vehicleType: 'Not Required',
        deadline: '',
        featured: false,
        urgent: false,
        coverImage: null
      });
      setNewCoverDims(null);
      setActiveTab('all-jobs');
      toast.success('Job created successfully!');
    } catch (err: any) {
      console.error('Create job failed:', err?.message || err);
      const msg = err?.data?.message || err?.message || 'Failed to create job';
      toast.error(msg);
    } finally {
      setIsCreatingJob(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { class: 'bg-green-100 text-green-800', label: 'Active' },
      pending: { class: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      rejected: { class: 'bg-red-100 text-red-800', label: 'Rejected' },
      expired: { class: 'bg-gray-100 text-gray-800', label: 'Expired' }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getTierBadge = (tier: string) => {
    const canonical = normalizeTier(tier) || 'latest';
    const tierConfig = jobTiers.find(t => t.key === canonical);
    if (!tierConfig) return <Badge variant="outline">{getTierLabel(canonical)}</Badge>;
    const Icon = tierConfig.icon;
    return (
      <Badge className={tierConfig.color}>
        <Icon className="h-3 w-3 mr-1" />
        {tierConfig.label}
      </Badge>
    );
  };

  const exportJobData = () => {
    try {
      const from = jobExportFrom ? new Date(jobExportFrom) : null;
      const to = jobExportTo ? new Date(jobExportTo) : null;

      const rows = enhancedJobs
        .filter((j: any) => {
          if (!from && !to) return true;
          const pd = new Date(j.postedDate || new Date().toISOString().slice(0, 10));
          if (from && pd < from) return false;
          if (to && pd > to) return false;
          return true;
        })
        .map((j: any) => ({
          title: j.title,
          company: j.companyName || j.company || '',
          tier: getTierLabel(normalizeTier(j.tier)),
          status: j.status || 'active',
          postedDate: j.postedDate || '',
          expiryDate: j.expiryDate || '',
          applications: j.applicationCount,
          views: j.viewCount,
          location: j.location || ''
        }));

      const esc = (v: any) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const header = `
        <tr>
          <th>Title</th>
          <th>Company</th>
          <th>Tier</th>
          <th>Status</th>
          <th>Location</th>
          <th>Posted</th>
          <th>Expiry</th>
          <th>Applications</th>
          <th>Views</th>
        </tr>`;
      const body = rows.map(r => `
        <tr>
          <td>${esc(r.title)}</td>
          <td>${esc(r.company)}</td>
          <td>${esc(r.tier)}</td>
          <td>${esc(r.status)}</td>
          <td>${esc(r.location)}</td>
          <td>${esc(r.postedDate)}</td>
          <td>${esc(r.expiryDate)}</td>
          <td>${esc(r.applications)}</td>
          <td>${esc(r.views)}</td>
        </tr>`).join('');

      const html = `<!DOCTYPE html><html><head><meta charset="UTF-8" /></head><body><table border="1">${header}${body}</table></body></html>`;
      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jobs_export_${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Jobs exported to Excel');
    } catch (e: any) {
      console.error('Export jobs error:', e);
      toast.error(e?.message || 'Failed to export jobs');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Job Post Management</h2>
          <p className="text-gray-600">Manage job postings, approval, and tier assignments</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input type="date" value={jobExportFrom} onChange={(e) => setJobExportFrom(e.target.value)} className="h-9" />
          <span className="text-sm text-gray-500">to</span>
          <Input type="date" value={jobExportTo} onChange={(e) => setJobExportTo(e.target.value)} className="h-9" />
          <Button variant="outline" onClick={exportJobData}>
            <Download className="h-4 w-4 mr-2" />
            Export Jobs
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-1">
        {jobTiers.map((tier) => {
          const tierJobs = tier.key === 'latest'
            ? enhancedJobs
            : enhancedJobs.filter(job => normalizeTier(job.tier) === tier.key);
          const activeCount = tierJobs.filter(j => j.status === 'active' && !isExpired(j)).length;
          const Icon = tier.icon;
          return (
            <Card key={tier.key}>
              <CardContent className="p-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-gray-600">{tier.label}</p>
                    <p className="text-sm font-bold">{activeCount}</p>
                    <p className="text-[10px] text-gray-500">{tier.price}</p>
                  </div>
                  <Icon className="h-3 w-3 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          );
        })}
        {(() => {
          const expiredCount = enhancedJobs.filter(j => isExpired(j) || j.status === 'expired').length;
          return (
            <Card>
              <CardContent className="p-1">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-medium text-gray-600">Expired Job</p>
                    <p className="text-sm font-bold">{expiredCount}</p>
                    <p className="text-[10px] text-gray-500">&nbsp;</p>
                  </div>
                  <Clock className="h-3 w-3 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {/* Active / Expired Tiles */}


      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-6">
      <div className="flex justify-center w-full mb-8">
        <Menubar className="h-12 bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-full p-1 shadow-lg shadow-gray-200/20 relative w-full">
          {[
            { id: 'all-jobs', label: 'All Jobs', icon: Briefcase },
            { id: 'add-job', label: 'Add Job', icon: Plus },
            { id: 'pending', label: 'Pending Approval', icon: Clock },
            { id: 'tier-management', label: 'Tier Management', icon: Star },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <MenubarMenu key={tab.id} className="flex-1">
                <MenubarTrigger
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    relative z-10 flex cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-2 text-sm font-medium transition-all duration-300 w-full
                    ${isActive ? 'text-white' : 'text-gray-500 hover:text-gray-900'}
                    bg-transparent border-none shadow-none focus:bg-transparent data-[state=open]:bg-transparent
                  `}
                >
                  <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  
                  {isActive && (
                    <motion.div
                      layoutId="activeJobTabIndicator"
                      className="absolute inset-0 bg-[#e37300] rounded-full shadow-sm"
                      style={{ zIndex: -1 }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                    />
                  )}
                </MenubarTrigger>
              </MenubarMenu>
            );
          })}
        </Menubar>
      </div>

        {/* Add Job */}
        <TabsContent value="add-job" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Create New Job Post</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Box label="Job Name">
                    <Input
                      id="jobTitle"
                      value={newJob.title}
                      onChange={(e) => setNewJob(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Job Name"
                      className={`${fieldCls} ${isInvalid('jobTitle') ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}
                    />
                  </Box>
                  <Box label="Select Employer">
                    <Select
                      value={newJob.company}
                      onValueChange={(val) => setNewJob(prev => ({ ...prev, company: val }))}
                    >
                      <SelectTrigger className={`${fieldCls} ${isInvalid('jobCompany') ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : ''}`}>
                        <SelectValue placeholder="Select Employer" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(company => (
                          <SelectItem key={company.id} value={company.name}>{company.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Box>
                </div>



                <div className="grid grid-cols-2 gap-3">
                  <BoxAuto label="">
                    <BranchSelector
                      companyName={newJob.company}
                      companies={companies}
                      selectedBranch={newJob.location}
                      onBranchSelect={(location) => { setNewJob(prev => ({ ...prev, location })); }}
                      onCompanyUpdate={onCompanyUpdate}
                      inputRef={locationRef}
                      invalid={isInvalid('jobLocation')}
                      showTextInput={false}
                    />
                  </BoxAuto>
                  <Box label="Desired Candidate">
                    <Select
                      value={newJob.desiredCandidate}
                      onValueChange={(val) => setNewJob(prev => ({ ...prev, desiredCandidate: val }))}
                    >
                      <SelectTrigger className={fieldCls}>
                        <SelectValue placeholder="Desired Candidate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                        <SelectItem value="Both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  </Box>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Box label="Pay Type">
                    <Select
                      value={newJob.payType}
                      onValueChange={(val) => setNewJob(prev => ({ ...prev, payType: val }))}
                    >
                      <SelectTrigger className={fieldCls}>
                        <SelectValue placeholder="Pay Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                        <SelectItem value="Weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </Box>
                  <Box label="Salary Type">
                    <Select
                      value={newJob.salaryType}
                      onValueChange={(val) => {
                        if (val === 'negotiable') {
                          setNewJob(prev => ({ ...prev, salaryType: 'negotiable', salaryMin: '', salaryMax: '' }));
                        } else {
                          setNewJob(prev => ({ ...prev, salaryType: 'range' }));
                        }
                      }}
                    >
                      <SelectTrigger className={fieldCls}>
                        <SelectValue placeholder="Salary Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="negotiable">Negotiable</SelectItem>
                        <SelectItem value="range">Range</SelectItem>
                      </SelectContent>
                    </Select>
                  </Box>
                </div>

                {newJob.salaryType === 'range' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Box label="Min Salary (NPR)">
                      <Input
                        id="salaryMin"
                        value={newJob.salaryMin}
                        onChange={(e) => setNewJob(prev => ({ ...prev, salaryMin: e.target.value }))}
                        placeholder="e.g. 50000"
                        className={fieldCls}
                      />
                    </Box>
                    <Box label="Max Salary (NPR)">
                      <Input
                        id="salaryMax"
                        value={newJob.salaryMax}
                        onChange={(e) => setNewJob(prev => ({ ...prev, salaryMax: e.target.value }))}
                        placeholder="e.g. 80000"
                        className={fieldCls}
                      />
                    </Box>
                    <Box label="Quick Range">
                      <Select
                        value={(newJob.salaryMin && newJob.salaryMax) ? `${newJob.salaryMin}-${newJob.salaryMax}` : ''}
                        onValueChange={(val) => {
                          if (!val) return;
                          const [min, max] = val.split('-');
                          setNewJob(prev => ({ ...prev, salaryType: 'range', salaryMin: min, salaryMax: max }));
                        }}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select quick range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="20000-30000">20,000-30,000</SelectItem>
                          <SelectItem value="30000-40000">30,000-40,000</SelectItem>
                          <SelectItem value="40000-50000">40,000-50,000</SelectItem>
                          <SelectItem value="50000-60000">50,000-60,000</SelectItem>
                          <SelectItem value="60000-70000">60,000-70,000</SelectItem>
                          <SelectItem value="70000-80000">70,000-80,000</SelectItem>
                          <SelectItem value="80000-100000">80,000-100,000</SelectItem>
                          <SelectItem value="100000-150000">100,000-150,000</SelectItem>
                          <SelectItem value="150000-200000">150,000-200,000</SelectItem>
                        </SelectContent>
                      </Select>
                    </Box>
                  </div>
                )}

                {/* Edit Salary Section */}
                {editingJob && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="editPayType">Pay Type</Label>
                      <Select
                        value={editingJob.payType || editingJob.pay_type || 'Monthly'}
                        onValueChange={(val) => setEditingJob((prev: any) => ({ ...prev, payType: val }))}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Pay Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editSalaryType">Salary Type</Label>
                      <Select
                        value={editingJob.salaryType || editingJob.salary_type || (editingJob.salaryMin && editingJob.salaryMax ? 'range' : 'negotiable')}
                        onValueChange={(val) => setEditingJob((prev: any) => ({ ...prev, salaryType: val }))}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Salary Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="range">Range</SelectItem>
                          <SelectItem value="negotiable">Negotiable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(String(editingJob.salaryType || '').toLowerCase() === 'range') && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="editSalaryMin">Min Salary (NPR)</Label>
                          <Input
                            id="editSalaryMin"
                            value={editingJob.salaryMin || editingJob.salary_min || ''}
                            onChange={(e) => setEditingJob((prev: any) => ({ ...prev, salaryMin: e.target.value }))}
                            placeholder="e.g. 50000"
                            className={fieldCls}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="editSalaryMax">Max Salary (NPR)</Label>
                          <Input
                            id="editSalaryMax"
                            value={editingJob.salaryMax || editingJob.salary_max || ''}
                            onChange={(e) => setEditingJob((prev: any) => ({ ...prev, salaryMax: e.target.value }))}
                            placeholder="e.g. 80000"
                            className={fieldCls}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="editQuickRange">Quick Range</Label>
                          <Select
                            value={((editingJob as any)?.salaryMin || (editingJob as any)?.salary_min) && ((editingJob as any)?.salaryMax || (editingJob as any)?.salary_max) ? `${(editingJob as any).salaryMin || (editingJob as any).salary_min}-${(editingJob as any).salaryMax || (editingJob as any).salary_max}` : ''}
                            onValueChange={(v) => {
                              if (!v) return;
                              const [min, max] = v.split('-');
                              setEditingJob((prev: any) => ({ ...prev, salaryType: 'range', salaryMin: min, salaryMax: max }));
                            }}
                          >
                            <SelectTrigger className={fieldCls}>
                              <SelectValue placeholder="Select quick range" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">Select quick range</SelectItem>
                              <SelectItem value="20000-30000">20,000-30,000</SelectItem>
                              <SelectItem value="30000-40000">30,000-40,000</SelectItem>
                              <SelectItem value="40000-50000">40,000-50,000</SelectItem>
                              <SelectItem value="50000-60000">50,000-60,000</SelectItem>
                              <SelectItem value="60000-70000">60,000-70,000</SelectItem>
                              <SelectItem value="70000-80000">70,000-80,000</SelectItem>
                              <SelectItem value="80000-100000">80,000-100,000</SelectItem>
                              <SelectItem value="100000-150000">100,000-150,000</SelectItem>
                              <SelectItem value="150000-200000">150,000-200,000</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}
                    {null}
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <Box label="Select Company Category">
                    <Select
                      value={newJob.tier}
                      onValueChange={(val) => setNewJob(prev => ({ ...prev, tier: normalizeTier(val) || 'latest' }))}
                    >
                      <SelectTrigger className={fieldCls}>
                        <SelectValue placeholder="Select Tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTiers.map(tier => (
                          <SelectItem key={tier.key} value={tier.key}>{tier.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Box>
                  <div className="space-y-2">
                    <Box label="Select Employee Type">
                      <Select
                        value={newJob.type}
                        onValueChange={(val) => setNewJob(prev => ({ ...prev, type: val }))}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select Employee Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full Time">Full Time</SelectItem>
                          <SelectItem value="Part Time">Part Time</SelectItem>
                          <SelectItem value="Contract">Contract</SelectItem>
                          <SelectItem value="Internship">Internship</SelectItem>
                        </SelectContent>
                      </Select>
                    </Box>
                  </div>
                  <div className="space-y-2">
                    <Box label="Select Experience">
                      <Select
                        value={newJob.experience}
                        onValueChange={(val) => setNewJob(prev => ({ ...prev, experience: val }))}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select Experience" />
                        </SelectTrigger>
                        <SelectContent>
                          {parameters.experience.map((exp) => (
                            <SelectItem key={exp.id} value={exp.name}>{exp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Box>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Box label="Select Job Level">
                    <Select
                      value={newJob.jobLevel}
                      onValueChange={(val) => setNewJob(prev => ({ ...prev, jobLevel: val }))}
                    >
                      <SelectTrigger className={fieldCls}>
                        <SelectValue placeholder="Select Job Level" />
                      </SelectTrigger>
                      <SelectContent>
                        {parameters.jobLevels.map((lvl) => (
                          <SelectItem key={lvl.id} value={lvl.name}>{lvl.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Box>

                  <Box label="Select License">
                    <Select
                      value={newJob.vehicleType}
                      onValueChange={(val) => setNewJob(prev => ({ ...prev, vehicleType: val, licenseRequired: val !== 'Not Required' }))}
                    >
                      <SelectTrigger className={fieldCls}>
                        <SelectValue placeholder="Select License" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Not Required">Not Required</SelectItem>
                        <SelectItem value="Two Wheeler">Two Wheeler</SelectItem>
                        <SelectItem value="Four Wheeler">Four Wheeler</SelectItem>
                        <SelectItem value="Company Vehicle">Company Vehicle</SelectItem>
                        <SelectItem value="Transport Allowance">Transport Allowance</SelectItem>
                        <SelectItem value="Fuel Allowance">Fuel Allowance</SelectItem>
                        <SelectItem value="Public Transport">Public Transport</SelectItem>
                        <SelectItem value="No Vehicle">No Vehicle</SelectItem>
                      </SelectContent>
                    </Select>
                  </Box>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  <BoxAuto label="Skills">
                    <div className="space-y-2">
                      <DropdownMenu open={skillsMenuOpen} onOpenChange={(o) => { setSkillsMenuOpen(o); if (!o) setSkillsQuery(''); }}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            className={`h-8 px-3 shadow-none ${Array.isArray(newJob.selectedSkills) && newJob.selectedSkills.length > 0 ? 'border-green-500 text-green-700 bg-green-50' : ''}`}
                            onClick={() => { setSkillsDraft(newJob.selectedSkills || []); setSkillsMenuOpen(true); }}
                          >
                            {Array.isArray(newJob.selectedSkills) && newJob.selectedSkills.length > 0
                              ? `${newJob.selectedSkills.length} selected`
                              : 'Select skills'}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="bottom" align="start" sideOffset={6} className="z-[10002] shadow-none border border-gray-200 rounded-md p-2 w-72">
                          <div className="mb-2">
                            <Input
                              placeholder="Search skills"
                              value={skillsQuery}
                              onChange={(e) => setSkillsQuery(e.target.value)}
                              className="h-8 text-xs"
                            />
                          </div>
                          <div className="space-y-1">
                            {parameters.skills
                              .filter((s) => s.name.toLowerCase().includes(skillsQuery.toLowerCase()))
                              .map((s) => (
                                <DropdownMenuCheckboxItem
                                  key={s.id}
                                  checked={skillsDraft.includes(s.name)}
                                  onCheckedChange={(checked) => {
                                    setSkillsDraft((prev) => {
                                      const set = new Set(prev);
                                      if (checked) set.add(s.name); else set.delete(s.name);
                                      return Array.from(set);
                                    });
                                  }}
                                  onSelect={(e) => e.preventDefault()}
                                  className="text-xs py-2 pr-2 pl-8 rounded data-[state=checked]:bg-green-50 data-[state=checked]:text-green-700"
                                >
                                  {s.name}
                                </DropdownMenuCheckboxItem>
                              ))}
                          </div>
                          <div className="flex items-center justify-between gap-2 mt-2">
                            <div className="text-[11px] text-gray-500">{skillsDraft.length} selected</div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSkillsDraft([]); setNewJob(prev => ({ ...prev, selectedSkills: [] })); setSkillsQuery(''); }}
                              >
                                Clear
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => { setNewJob(prev => ({ ...prev, selectedSkills: skillsDraft })); setSkillsMenuOpen(false); }}
                              >
                                Add Selected
                              </Button>
                            </div>
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <div className="flex flex-wrap gap-1">
                        {(newJob.selectedSkills || []).slice(0, 3).map((sk: string) => (
                          <Badge key={sk} variant="secondary" className="text-xs">{sk}</Badge>
                        ))}
                        {Array.isArray(newJob.selectedSkills) && newJob.selectedSkills.length > 3 && (
                          <span className="text-xs text-gray-500">+{newJob.selectedSkills.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  </BoxAuto>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  <BoxAuto label="Job Description">
                    <RichTextEditor
                      value={newJob.description}
                      onChange={(val) => setNewJob(prev => ({ ...prev, description: val }))}
                      placeholder="Enter job description"
                      className="min-h-[300px]"
                    />
                  </BoxAuto>

                  <BoxAuto label="Job Specification">
                    <RichTextEditor
                      value={newJob.requirements}
                      onChange={(val) => setNewJob(prev => ({ ...prev, requirements: val }))}
                      placeholder="Enter job specifications"
                      className="min-h-[300px]"
                    />
                  </BoxAuto>
                </div>

                <Box label="Expires on">
                  <Input
                    id="jobDeadline"
                    type="date"
                    value={newJob.deadline}
                    onChange={(e) => setNewJob(prev => ({ ...prev, deadline: e.target.value }))}
                    className={fieldCls}
                  />
                </Box>

                {/* Job Cover Image Upload */}
                <div className="space-y-2">
                  <Label>Job Cover Image</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-white">
                    {newJob.coverImage ? (
                      <div className="space-y-2">
                        <div className="w-full h-32 mx-auto bg-gray-100 rounded-md flex items-center justify-center overflow-hidden">
                          <ImageWithFallback
                            src={URL.createObjectURL(newJob.coverImage)}
                            alt="Job Cover"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <p className="text-sm text-gray-600">{newJob.coverImage.name}</p>
                        {newCoverDims && (
                          <p className="text-xs text-gray-500">Selected size: {newCoverDims.w}x{newCoverDims.h}px • Recommended: 1200x600px • Max: 5MB</p>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => { setNewJob({ ...newJob, coverImage: null }); setNewCoverDims(null); }}
                        >
                          Remove Image
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600">Upload job cover image</p>
                        <p className="text-sm text-gray-500">JPG, PNG up to 5MB. Recommended size: 1200x600px</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setNewJob({ ...newJob, coverImage: file });
                            setNewCoverDims(null);
                            if (file) {
                              const img = new Image();
                              img.onload = () => setNewCoverDims({ w: img.naturalWidth, h: img.naturalHeight });
                              img.src = URL.createObjectURL(file);
                            }
                          }}
                          className="hidden"
                          id="job-cover-upload-inline"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('job-cover-upload-inline')?.click()}
                          className="mt-2"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose Image
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setNewJob({
                      title: '',
                      company: '',
                      location: '',
                      description: '',
                      requirements: '',
                      salary: '',
                      salaryType: 'exact',
                      salaryMin: '',
                      salaryMax: '',
                      payType: 'Monthly',
                      tier: 'latest',
                      type: 'Full Time',
                      category: '',
                      experience: '',
                      jobLevel: '',
                      desiredCandidate: '',
                      skillCategory: '',
                      skillName: '',
                      selectedSkills: [] as string[],
                      licenseRequired: false,
                      vehicleType: 'Not Required',
                      deadline: '',
                      featured: false,
                      urgent: false,
                      coverImage: null
                    });
                    setNewCoverDims(null);
                  }}>Reset</Button>
                  <Button onClick={handleCreateJob} disabled={isCreatingJob}>
                    {isCreatingJob ? 'Creating...' : 'Create Job'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Jobs */}
        <TabsContent value="all-jobs" className="space-y-6">
          {/* Search and Filters */}
          <Card>
            <CardContent className="p-2">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    placeholder="Search jobs, companies, locations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px] h-9 px-3 bg-white">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={tierFilter} onValueChange={setTierFilter}>
                    <SelectTrigger className="w-[180px] h-9 px-3 bg-white">
                      <SelectValue placeholder="All Tiers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tiers</SelectItem>
                      {jobTiers.map(tier => (
                        <SelectItem key={tier.key} value={tier.key}>{tier.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={companyFilter} onValueChange={setCompanyFilter}>
                    <SelectTrigger className="w-[200px] h-9 px-3 bg-white">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Companies</SelectItem>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.name}>{company.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Edit Job Inline */}
          {editingJob && (
            <Card className="fixed inset-0 z-[100] bg-white overflow-y-auto">
              <CardHeader className="pb-2 sticky top-0 bg-white border-b z-10">
                <CardTitle>Edit Job</CardTitle>
                <div className="text-sm text-gray-600">Update the job posting details.</div>
              </CardHeader>
              <CardContent className="pb-20">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Box label="Job Title">
                      <Input id="edit-title" value={editingJob.title}
                        onChange={(e) => setEditingJob({ ...editingJob, title: e.target.value })}
                        className={fieldCls} />
                    </Box>
                    <Box label="Company">
                      <Input id="edit-company" value={editingJob.company} onChange={(e) => { setEditingJob({ ...editingJob, company: e.target.value }); }} className={fieldCls} />
                    </Box>
                    <div className="md:col-span-1">
                      <BoxAuto label="Location / Branch">
                        <BranchSelector
                          companyName={editingJob.company}
                          companies={companies}
                          selectedBranch={editingJob.location}
                          onBranchSelect={(location) => setEditingJob({ ...editingJob, location })}
                          onCompanyUpdate={onCompanyUpdate}
                          variant="compact"
                          showLabel={false}
                          showTextInput={false}
                        />
                      </BoxAuto>
                    </div>
                    <div className="md:col-span-1">
                      <Box label="Desired Candidate">
                        <Select
                          value={(editingJob as any).desiredCandidate || (editingJob as any).desired_candidate || ''}
                          onValueChange={(val) => setEditingJob({ ...editingJob, desiredCandidate: val, desired_candidate: val })}
                        >
                          <SelectTrigger className={fieldCls}>
                            <SelectValue placeholder="Desired Candidate" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Others">Others</SelectItem>
                            <SelectItem value="Both">Both</SelectItem>
                          </SelectContent>
                        </Select>
                      </Box>
                    </div>
                    <Box label="Tier">
                      <Select
                        value={editingJob.tier}
                        onValueChange={(val) => setEditingJob({ ...editingJob, tier: normalizeTier(val) || 'latest' })}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select Tier" />
                        </SelectTrigger>
                        <SelectContent>
                          {jobTiers.map(tier => (
                            <SelectItem key={tier.key} value={tier.key}>{tier.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Box>
                    <Box label="Select Employee Type">
                      <Select
                        value={(editingJob as any).type || 'Full Time'}
                        onValueChange={(val) => setEditingJob({ ...editingJob, type: val })}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select Employee Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full Time">Full Time</SelectItem>
                          <SelectItem value="Part Time">Part Time</SelectItem>
                          <SelectItem value="Contract">Contract</SelectItem>
                          <SelectItem value="Internship">Internship</SelectItem>
                        </SelectContent>
                      </Select>
                    </Box>
                    <Box label="Select Experience">
                      <Select
                        value={(editingJob as any).experience || ''}
                        onValueChange={(val) => setEditingJob({ ...editingJob, experience: val })}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select Experience" />
                        </SelectTrigger>
                        <SelectContent>
                          {parameters.experience.map((exp: any) => (
                            <SelectItem key={exp.id} value={exp.name}>{exp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Box>
                    <div className="md:col-span-2">
                      <BoxAuto label="Job Description">
                        <RichTextEditor
                          value={editingJob.description || ''}
                          onChange={(val) => setEditingJob({ ...editingJob, description: val })}
                          placeholder="Enter job description"
                          className="min-h-[300px]"
                        />
                      </BoxAuto>
                    </div>
                    <div className="md:col-span-2">
                      <BoxAuto label="Job Specification">
                        <RichTextEditor
                          value={editingJob.requirements || ''}
                          onChange={(val) => setEditingJob({ ...editingJob, requirements: val })}
                          placeholder="Enter job specifications"
                          className="min-h-[300px]"
                        />
                      </BoxAuto>
                    </div>
                    <div className="md:col-span-2">
                      <BoxAuto label="Skills">
                        <div className="space-y-2">
                          <DropdownMenu open={skillsMenuOpen} onOpenChange={(o) => { setSkillsMenuOpen(o); if (!o) setSkillsQuery(''); }}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                className={`h-8 px-3 shadow-none ${((Array.isArray((editingJob as any).selectedSkills) && (editingJob as any).selectedSkills.length) || (Array.isArray((editingJob as any).skills) && (editingJob as any).skills.length)) ? 'border-green-500 text-green-700 bg-green-50' : ''}`}
                                onClick={() => { setSkillsDraft(((editingJob as any).selectedSkills || (editingJob as any).skills || []) as string[]); setSkillsMenuOpen(true); }}
                              >
                                {Array.isArray((editingJob as any).selectedSkills) && (editingJob as any).selectedSkills.length > 0
                                  ? `${(editingJob as any).selectedSkills.length} selected`
                                  : (Array.isArray((editingJob as any).skills) && (editingJob as any).skills.length > 0
                                    ? `${(editingJob as any).skills.length} selected`
                                    : 'Select skills')}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="bottom" align="start" sideOffset={6} className="z-[10002] shadow-none border border-gray-200 rounded-md p-2 w-72">
                              <div className="mb-2">
                                <Input
                                  placeholder="Search skills"
                                  value={skillsQuery}
                                  onChange={(e) => setSkillsQuery(e.target.value)}
                                  className="h-8 text-xs"
                                />
                              </div>
                              <div className="space-y-1">
                                {parameters.skills
                                  .filter((s: any) => s.name.toLowerCase().includes(skillsQuery.toLowerCase()))
                                  .map((s: any) => (
                                    <DropdownMenuCheckboxItem
                                      key={s.id}
                                      checked={skillsDraft.includes(s.name)}
                                      onCheckedChange={(checked) => {
                                        setSkillsDraft((prev) => {
                                          const set = new Set(prev);
                                          if (checked) set.add(s.name); else set.delete(s.name);
                                          return Array.from(set) as string[];
                                        });
                                      }}
                                      onSelect={(e) => e.preventDefault()}
                                      className="text-xs py-2 pr-2 pl-8 rounded data-[state=checked]:bg-green-50 data-[state=checked]:text-green-700"
                                    >
                                      {s.name}
                                    </DropdownMenuCheckboxItem>
                                  ))}
                              </div>
                              <div className="flex items-center justify-between gap-2 mt-2">
                                <div className="text-[11px] text-gray-500">{skillsDraft.length} selected</div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setSkillsDraft([]); setEditingJob((prev: any) => ({ ...prev, selectedSkills: [] })); setSkillsQuery(''); }}
                                  >
                                    Clear
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => { setEditingJob((prev: any) => ({ ...prev, selectedSkills: skillsDraft })); setSkillsMenuOpen(false); }}
                                  >
                                    Add Selected
                                  </Button>
                                </div>
                              </div>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <div className="flex flex-wrap gap-1">
                            {(((editingJob as any).selectedSkills || []) as string[]).slice(0, 3).map((sk: string) => (
                              <Badge key={sk} variant="secondary" className="text-xs">{sk}</Badge>
                            ))}
                            {Array.isArray((editingJob as any).selectedSkills) && (editingJob as any).selectedSkills.length > 3 && (
                              <span className="text-xs text-gray-500">+{(editingJob as any).selectedSkills.length - 3} more</span>
                            )}
                          </div>
                        </div>
                      </BoxAuto>
                    </div>
                    <Box label="Pay Type">
                      <Select
                        value={editingJob.payType || editingJob.pay_type || 'Monthly'}
                        onValueChange={(val) => setEditingJob({ ...editingJob, payType: val })}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Pay Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </Box>
                    <Box label="Salary Type">
                      <Select
                        value={editingJob.salaryType || editingJob.salary_type || (editingJob.salaryMin && editingJob.salaryMax ? 'range' : (editingJob.salary ? 'exact' : 'negotiable'))}
                        onValueChange={(val) => setEditingJob({ ...editingJob, salaryType: val })}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Salary Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="exact">Exact amount</SelectItem>
                          <SelectItem value="range">Range</SelectItem>
                          <SelectItem value="negotiable">Negotiable</SelectItem>
                        </SelectContent>
                      </Select>
                    </Box>
                    {String(editingJob.salaryType || editingJob.salary_type || '').toLowerCase() === 'range' && (
                      <>
                        <Box label="Min Salary (NPR)">
                          <Input
                            id="edit-salary-min"
                            ref={editSalaryMinRef}
                            value={editingJob.salaryMin || editingJob.salary_min || ''}
                            onChange={(e) => { setEditingJob({ ...editingJob, salaryMin: e.target.value }); }}
                            placeholder="e.g. 50000"
                            className={fieldCls}
                          />
                        </Box>
                        <Box label="Max Salary (NPR)">
                          <Input
                            id="edit-salary-max"
                            ref={editSalaryMaxRef}
                            value={editingJob.salaryMax || editingJob.salary_max || ''}
                            onChange={(e) => { setEditingJob({ ...editingJob, salaryMax: e.target.value }); }}
                            placeholder="e.g. 80000"
                            className={fieldCls}
                          />
                        </Box>
                      </>
                    )}
                    {String(editingJob.salaryType || editingJob.salary_type || '').toLowerCase() === 'exact' && (
                      <div className="md:col-span-2">
                        <Box label="Salary (NPR)">
                          <Input
                            id="edit-salary-exact"
                            value={editingJob.salary || ''}
                            onChange={(e) => setEditingJob({ ...editingJob, salary: e.target.value })}
                            placeholder="e.g. 60000"
                            className={fieldCls}
                          />
                        </Box>
                      </div>
                    )}
                    <Box label="Select Job Level">
                      <Select
                        value={(editingJob as any).jobLevel || ''}
                        onValueChange={(val) => setEditingJob({ ...editingJob, jobLevel: val })}
                      >
                        <SelectTrigger className={fieldCls}>
                          <SelectValue placeholder="Select Job Level" />
                        </SelectTrigger>
                        <SelectContent>
                          {parameters.jobLevels.map((lvl: any) => (
                            <SelectItem key={lvl.id} value={lvl.name}>{lvl.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Box>
                    <div className="space-y-2">
                      <Label htmlFor="edit-vehicleType">Select License</Label>
                      <Box label="Select License">
                        <Select
                          value={editingJob.vehicleType || 'Not Required'}
                          onValueChange={(val) => setEditingJob({ ...editingJob, vehicleType: val, licenseRequired: val !== 'Not Required' })}
                        >
                          <SelectTrigger className={fieldCls}>
                            <SelectValue placeholder="Select License" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Not Required">Not Required</SelectItem>
                            <SelectItem value="Two Wheeler">Two Wheeler</SelectItem>
                            <SelectItem value="Four Wheeler">Four Wheeler</SelectItem>
                            <SelectItem value="Company Vehicle">Company Vehicle</SelectItem>
                            <SelectItem value="Transport Allowance">Transport Allowance</SelectItem>
                            <SelectItem value="Fuel Allowance">Fuel Allowance</SelectItem>
                            <SelectItem value="Public Transport">Public Transport</SelectItem>
                            <SelectItem value="No Vehicle">No Vehicle</SelectItem>
                          </SelectContent>
                        </Select>
                      </Box>
                    </div>
                    <Box label="Expires on">
                      <Input
                        id="edit-deadline"
                        type="date"
                        value={(editingJob as any).expiryDate || (editingJob as any).deadline || ''}
                        onChange={(e) => setEditingJob((prev: any) => ({ ...prev, expiryDate: e.target.value }))}
                        className={fieldCls}
                      />
                    </Box>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="edit-cover">Cover Image</Label>
                      <div className="flex items-center gap-4">
                        {((editingJob as any).coverImageUrl || (editingJob as any).cover_image_url) ? (
                          <ImageWithFallback
                            src={normalizeMediaUrl((editingJob as any).coverImageUrl || (editingJob as any).cover_image_url) || ((editingJob as any).coverImageUrl || (editingJob as any).cover_image_url)}
                            fallbackSrc={'/CompanyLogo.png'}
                            alt="Current cover"
                            className="h-20 w-auto rounded"
                          />
                        ) : null}
                        <input
                          id="edit-cover"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setEditingCoverFile(e.target.files?.[0] || null)}
                        />
                      </div>
                      <Input
                        placeholder="Cover image URL"
                        value={(editingJob as any).coverImageUrl || (editingJob as any).cover_image_url || ''}
                        onChange={(e) => setEditingJob((prev: any) => ({ ...prev, coverImageUrl: e.target.value, cover_image_url: e.target.value }))}
                      />
                      <p className="text-xs text-gray-500">Use a wide image (~1600x600) for best results.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <div className="sticky bottom-0 bg-white border-t flex justify-end gap-2 p-4">
                <Button variant="outline" onClick={() => setEditingJob(null)} disabled={isUpdatingJob}>Cancel</Button>
                <Button onClick={handleUpdateJob} disabled={isUpdatingJob}>{isUpdatingJob ? 'Updating…' : 'Update Job'}</Button>
              </div>
            </Card>
          )}

          {/* Jobs Table */}
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle>Jobs ({filteredJobs.length})</CardTitle>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setActiveTab('add-job')}>
                <Upload className="h-4 w-4 mr-2" />
                Add Job
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 px-2 text-xs">#</th>
                      <th className="text-left py-1 px-2 text-xs">Job</th>
                      <th className="text-left py-1 px-2 text-xs">Company</th>
                      <th className="text-left py-1 px-2 text-xs">Tier</th>
                      <th className="text-left py-1 px-2 text-xs">Salary</th>
                      <th className="text-left py-1 px-2 text-xs">Views</th>
                      <th className="text-left py-1 px-2 text-xs">License</th>
                      <th className="text-left py-1 px-2 text-xs">Status</th>
                      <th className="text-left py-1 px-2 text-xs">Posted</th>
                      <th className="text-left py-1 px-2 text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedJobs.map((job, idx) => (
                      <tr key={job.id} className="border-b hover:bg-gray-50 odd:bg-white even:bg-orange-50/40">
                        <td className="py-1 px-2 text-xs text-gray-600 whitespace-nowrap">
                          {(currentPage - 1) * itemsPerPage + idx + 1}
                        </td>
                        <td className="py-1 px-2">
                          <div className="flex items-center space-x-2 whitespace-nowrap">
                            {getJobImage(job) ? (
                              <ImageWithFallback
                                src={getJobImage(job)}
                                fallbackSrc={'/CompanyLogo.png'}
                                alt={job.title}
                                className="w-6 h-6 object-cover"
                              />
                            ) : (
                              <div className="w-6 h-6 bg-gray-100 flex items-center justify-center">
                                <ImageIcon className="h-3 w-3 text-gray-400" />
                              </div>
                            )}
                            <span className="text-xs font-medium truncate max-w-[240px]" title={job.title}>
                              {job.title}
                            </span>
                          </div>
                        </td>
                        <td className="py-1 px-2 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span className="text-xs truncate max-w-[160px]" title={job.company}>{job.company}</span>
                          </div>
                        </td>
                        <td className="py-1 px-2 whitespace-nowrap">
                          {getTierBadge(job.tier)}
                        </td>
                        <td className="py-1 px-2 text-xs text-gray-700 whitespace-nowrap">
                          <span className="truncate max-w-[180px] block" title={getSalaryDisplay(job)}>
                            {getSalaryDisplay(job)}
                          </span>
                        </td>
                        <td className="py-1 px-2 whitespace-nowrap">
                          <Badge variant="outline">{job.viewCount}</Badge>
                        </td>
                        <td className="py-1 px-2 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={job.licenseRequired ? 'bg-blue-50' : ''}>
                              {job.licenseRequired ? 'Required' : 'Not Required'}
                            </Badge>
                            {job.licenseRequired && job.vehicleType && (
                              <Badge variant="outline" className="bg-blue-50">
                                {job.vehicleType}
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-1 px-2 whitespace-nowrap">
                          {getStatusBadge(
                            job.status === 'rejected' ? 'rejected' :
                              job.status === 'pending' ? 'pending' :
                                isExpired(job) ? 'expired' :
                                  job.status
                          )}
                        </td>
                        <td className="py-1 px-2 text-xs text-gray-600 whitespace-nowrap">
                          {job.postedDate}
                        </td>
                        <td className="py-1 px-2 whitespace-nowrap">
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => setViewingJob(job)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setEditingJob({ ...job })}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {job.status === 'pending' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleApproveJob(job.id)}
                                className="text-green-600 hover:text-green-700"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteDialog({ open: true, jobId: (job.id || (job as any)._id), jobTitle: job.title })}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
              {filteredJobs.length > 0 && (
                <div className="flex items-center justify-between px-4 py-3 border-t mt-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span>Show</span>
                    <Select
                      value={String(itemsPerPage)}
                      onValueChange={(val) => setItemsPerPage(Number(val))}
                    >
                      <SelectTrigger className="w-[80px] h-8 bg-white border">
                        <SelectValue placeholder={String(itemsPerPage)} />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 25, 50, 100].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span>entries</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Showing {filteredJobs.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}
                    {" "}to{" "}
                    {Math.min(currentPage * itemsPerPage, filteredJobs.length)} of {filteredJobs.length} entries
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).slice(0, 7).map(page => (
                      <Button
                        key={page}
                        variant={page === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Button>
                    ))}
                    {totalPages > 7 && (
                      <>
                        <span className="px-2 text-gray-500">...</span>
                        <Button
                          variant={totalPages === currentPage ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approval */}
        <TabsContent value="pending" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pending Job Approvals ({enhancedJobs.filter(j => j.status === 'pending').length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {enhancedJobs.filter(j => j.status === 'pending').map(job => (
                  <div key={job.id} className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div>
                            <h3 className="font-semibold text-lg">{job.title}</h3>
                            <p className="text-gray-600">{job.company} • {job.location}</p>
                          </div>
                          {getTierBadge(job.tier)}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          {job.description || "No description provided."}
                        </p>
                        <div className="flex gap-4 text-sm text-gray-600">
                          <span>Posted: {job.postedDate}</span>
                          <span>Expires: {job.expiryDate}</span>
                          <span>Salary: {job.salary || 'Not specified'}</span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          onClick={() => handleApproveJob(job.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleRejectJob(job.id)}
                          className="text-red-600 hover:text-red-700 border-red-200"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tier Management */}
        <TabsContent value="tier-management" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Job Tier Management</CardTitle>
              <p className="text-sm text-gray-600">Configure job posting tiers and pricing</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {jobTiers.map((tier) => {
                  const tierJobs = tier.key === 'latest'
                    ? enhancedJobs
                    : enhancedJobs.filter(job => normalizeTier(job.tier) === tier.key);
                  const Icon = tier.icon;
                  return (
                    <Card key={tier.key} className="relative">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <Icon className="h-8 w-8 text-gray-600" />
                          <div>
                            <h3 className="font-semibold">{tier.label}</h3>
                            <p className="text-sm text-gray-600">{tier.price}</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                          {tier.description}
                        </p>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Active Jobs:</span>
                            <span className="font-medium">{tierJobs.filter(j => j.status === 'active' && !isExpired(j)).length}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Total Applications:</span>
                            <span className="font-medium">
                              {tierJobs.reduce((sum, job) => sum + job.applicationCount, 0)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Revenue:</span>
                            <span className="font-medium">
                              {typeof tier.price === 'string' && tier.price.toLowerCase().includes('free')
                                ? 'Free'
                                : `Rs. ${(tierJobs.length * (parseInt(String(tier.price).replace(/[^\d]/g, '')) || 0)).toLocaleString()}`}
                            </span>
                          </div>
                        </div>
                        <Button variant="outline" className="w-full mt-4">
                          Configure Tier
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* View Job Modal */}
        <Dialog open={!!viewingJob} onOpenChange={(open) => { if (!open) setViewingJob(null); }}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{viewingJob?.title || 'Job Details'}</DialogTitle>
              <DialogDescription>Quick view of job posting.</DialogDescription>
            </DialogHeader>
            {viewingJob && (
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="flex items-center gap-2">{getTierBadge(viewingJob.tier)}</div>
                <div className="border-b pb-2">
                  <h4 className="font-semibold text-gray-900">{viewingJob.company}</h4>
                  <p className="text-sm text-gray-600">{viewingJob.location}</p>
                </div>

                <div className="space-y-2">
                  <h5 className="font-bold text-sm text-gray-900 uppercase tracking-wider">Description</h5>
                  <div
                    className="text-sm text-gray-700 leading-relaxed rich-content"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(decodeHtml(viewingJob.description || 'No description provided.')) }}
                  />
                </div>

                {viewingJob.requirements && (
                  <div className="space-y-2">
                    <h5 className="font-bold text-sm text-gray-900 uppercase tracking-wider">Requirements</h5>
                    <div
                      className="text-sm text-gray-700 leading-relaxed rich-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(decodeHtml(
                          Array.isArray(viewingJob.requirements)
                            ? viewingJob.requirements.join('<br/>')
                            : viewingJob.requirements
                        ))
                      }}
                    />
                  </div>
                )}

                {(viewingJob.desired_candidate || viewingJob.desiredCandidate) && (
                  <div className="space-y-2">
                    <h5 className="font-bold text-sm text-gray-900 uppercase tracking-wider">Desired Candidate</h5>
                    <div
                      className="text-sm text-gray-700 leading-relaxed rich-content"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(decodeHtml(viewingJob.desired_candidate || viewingJob.desiredCandidate))
                      }}
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-xs bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex flex-col">
                    <span className="text-gray-500">Applications</span>
                    <span className="font-medium">{viewingJob.applicationCount || 0}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-500">Views</span>
                    <span className="font-medium">{viewingJob.viewCount || 0}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-500">Status</span>
                    <Badge variant="outline" className="w-fit h-5 text-[10px] mt-1">{viewingJob.status}</Badge>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-500">Salary</span>
                    <span className="font-medium">{getSalaryDisplay(viewingJob)}</span>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewingJob(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>



        <ConfirmDialog
          open={deleteDialog.open}
          onOpenChange={(open) => !isDeleting && setDeleteDialog({ open, jobId: null, jobTitle: '' })}
          onConfirm={handleDeleteJob}
          title="Delete Job Posting"
          description="Are you sure you want to permanently delete this job posting? All applications and related data will be removed."
          confirmText="Delete Job"
          cancelText="Cancel"
          type="danger"
          itemName={deleteDialog.jobTitle}
          isLoading={isDeleting}
          variant="minimal"
        />

        {/* Edit Job Inline */}

      </Tabs>
    </div>
  );


}
